import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";

const fullName = (ln, fn) => [ln.trim().toUpperCase(), fn.trim()].filter(Boolean).join(" ");

export const NamesDialog = ({ open, onOpenChange, list, splitName, onDone }) => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setRows(list.map((r) => {
      const s = splitName(r);
      return { id: r.id, email: r.email, current: r.name || "", last_name: s.last_name.toUpperCase(), first_name: s.first_name, on: true };
    }).filter((x) => fullName(x.last_name, x.first_name) !== x.current.trim() || !list.find((r) => r.id === x.id).last_name)
      .sort((a, b) => a.last_name.localeCompare(b.last_name, "fr")));
  }, [open, list, splitName]);
  const set = (id, patch) => setRows((rs) => rs.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  const picked = rows.filter((x) => x.on && x.last_name.trim());
  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/resellers/names", { items: picked.map(({ id, last_name, first_name }) => ({ id, last_name, first_name })) });
      toast.success(`${data.updated} nom(s) harmonisé(s)`);
      onOpenChange(false); onDone?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-1.5rem)] max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="names-dialog">
        <DialogHeader><DialogTitle>Harmoniser les noms au format « NOM Prénom »</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">Vérifiez chaque proposition : corrigez le nom ou le prénom si la séparation est fausse, ou décochez la ligne pour la laisser telle quelle.</p>
        <div className="text-xs text-muted-foreground" data-testid="names-count">{rows.length} nom(s) à harmoniser · {picked.length} coché(s)</div>
        <div className="border rounded-md divide-y">
          {!rows.length && <div className="px-3 py-4 text-sm text-muted-foreground" data-testid="names-empty">Tous les noms sont déjà au bon format.</div>}
          {rows.map((x) => (
            <div key={x.id} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm" data-testid={`names-row-${x.email}`}>
              <Checkbox checked={x.on} onCheckedChange={(v) => set(x.id, { on: !!v })} data-testid={`names-check-${x.email}`} />
              <span className="w-44 truncate text-muted-foreground" title={x.current}>{x.current || "—"}</span>
              <ArrowRight className="w-3.5 h-3.5 text-muted-foreground" />
              <Input value={x.last_name} onChange={(e) => set(x.id, { last_name: e.target.value.toUpperCase() })} placeholder="NOM" className="h-8 w-44" data-testid={`names-last-${x.email}`} />
              <Input value={x.first_name} onChange={(e) => set(x.id, { first_name: e.target.value })} placeholder="Prénom" className="h-8 w-36" data-testid={`names-first-${x.email}`} />
              <button type="button" className="text-xs underline text-muted-foreground" onClick={() => set(x.id, { last_name: x.first_name.toUpperCase(), first_name: x.last_name.charAt(0) + x.last_name.slice(1).toLowerCase() })} data-testid={`names-swap-${x.email}`}>Inverser</button>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="names-cancel">Annuler</Button>
          <Button onClick={apply} disabled={busy || !picked.length} className="gap-2" data-testid="names-apply">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Valider {picked.length} nom(s)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
